import { Check } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useOpenEncounter } from '@/features/encounters/api';
import { useQueue, useQueueActions } from '@/features/queue/api';
import type { QueueTicket } from '@/features/queue/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import { callNextFailure } from '@/pages/queue/call-next-error';
import { RoomDialog } from '@/pages/queue/RoomDialog';
import styles from '@/pages/queue/queue.module.css';
import { PatientSays } from '@/features/queue/PatientSays';
import { myPatientRows } from './consult-utils';

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
const STATUS: Record<QueueTicket['status'], string> = {
  waiting: 'Waiting',
  in_room: 'In room',
  done: 'Done',
  cancelled: 'Cancelled',
};

/**
 * Admin v2 Doctor "My patients today" (#68): this doctor's list with service,
 * payer, arrival and status. Patients the front desk called (no doctor yet)
 * stay visible to the branch's doctors until one opens them; opening takes
 * the patient (RL-707 blocker, locked decision).
 */
export function MyPatientsPage({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { me, hasPermission } = useSession();
  const { data, isLoading, error, refetch } = useQueue();
  const { callNext } = useQueueActions();
  const open = useOpenEncounter();
  const navigate = useNavigate();
  const toast = useToast();
  const [drawer, setDrawer] = useUrlState<'closed' | 'call-next'>(
    'drawer',
    'closed',
    ['closed', 'call-next'],
  );
  const calling = drawer === 'call-next';
  const [status, setStatus] = useUrlState('status', 'all');
  const [service, setService] = useUrlState('service', 'all');
  const [payer, setPayer] = useUrlState('payer', 'all');
  const [search, setSearch] = useUrlState('q', '', undefined, {
    replace: true,
  });
  const fail = (err: unknown) => {
    const alreadyActive =
      isApiError(err) && err.code === 'ACTIVE_PATIENT_EXISTS';
    toast.show({
      tone: 'error',
      ...callNextFailure(err, t),
      ...(alreadyActive && { title: t('myPatients.activePatientExists') }),
      error: err,
    });
  };

  if (!branch) {
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose the branch you are working at in the branch switch.
        </p>
      </PageContainer>
    );
  }

  const allRows = myPatientRows(data?.tickets ?? [], me?.id);
  const services = [
    ...new Map(
      allRows
        .filter((ticket) => ticket.service)
        .map((ticket) => [ticket.service!.id, ticket.service!.name]),
    ),
  ];
  const payers = [
    ...new Map(
      allRows.map((ticket) => [
        ticket.payer?.id ?? 'self',
        ticket.payer?.name ?? t('myPatients.selfPay'),
      ]),
    ),
  ];
  const term = search.trim().toLocaleLowerCase();
  const rows = allRows.filter(
    (ticket) =>
      (status === 'all' || ticket.status === status) &&
      (service === 'all' || ticket.service?.id === service) &&
      (payer === 'all' || (ticket.payer?.id ?? 'self') === payer) &&
      (!term ||
        ticket.patient.fullName.toLocaleLowerCase().includes(term) ||
        ticket.patient.identity?.value.toLocaleLowerCase().includes(term)),
  );
  const active = allRows.find(
    (ticket) =>
      ticket.status === 'in_room' &&
      ticket.doctor?.id === me?.id &&
      ticket.encounter?.status !== 'closed',
  );

  const columns: Column<QueueTicket>[] = [
    { key: 'no', header: 'No.', render: (t) => <strong>{t.number}</strong> },
    {
      key: 'patient',
      header: 'Patient',
      render: (t) => (
        <>
          <p className={styles.name}>{t.patient.fullName}</p>
          <PatientSays reason={t.patientReason} compact />
          <p className={styles.sub}>
            <PatientIdentityText identity={t.patient.identity} />
          </p>
        </>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      render: (t) => t.service?.name ?? '—',
    },
    {
      key: 'payer',
      header: 'Payment method',
      render: (t) => t.payer?.name ?? 'Self-pay',
    },
    { key: 'arrived', header: 'Arrived', render: (t) => time(t.arrivedAt) },
    {
      key: 'status',
      header: 'Status',
      render: (t) => (
        <div className={styles.badges}>
          <Badge tone={t.status === 'in_room' ? 'success' : 'neutral'}>
            {t.status === 'in_room' ? `In ${t.room}` : STATUS[t.status]}
          </Badge>
          {t.status === 'in_room' && !t.doctor && (
            <Badge tone="warning">Called by the desk</Badge>
          )}
          {t.seeFirst && <Badge tone="danger">See first</Badge>}
          {!t.vitals && t.status !== 'done' && <Badge>No vitals</Badge>}
          {t.vitals?.flags.length ? (
            <Badge tone="danger">Vitals flagged</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (ticket) => (
        <div className={styles.rowActions}>
          <Link
            className={styles.linkButton}
            to={`/patients/${ticket.patient.id}`}
          >
            Record
          </Link>
          {hasPermission('encounters:write') &&
            ticket.status !== 'done' &&
            !ticket.encounter && (
              <Button
                size="sm"
                disabled={!!active && active.id !== ticket.id}
                title={
                  active && active.id !== ticket.id
                    ? t('myPatients.finishActiveFirst', {
                        number: active.number,
                      })
                    : undefined
                }
                loading={open.isPending && open.variables === ticket.id}
                onClick={() =>
                  open
                    .mutateAsync(ticket.id)
                    .then((e) => navigate(`/consultations/${e.id}`))
                    .catch(fail)
                }
              >
                Open
              </Button>
            )}
          {ticket.status !== 'done' && ticket.encounter && (
            <Link
              className={styles.linkButton}
              to={`/consultations/${ticket.encounter.id}`}
            >
              {t('myPatients.continueEncounter')}
            </Link>
          )}
          {ticket.status === 'done' && ticket.encounter && (
            <Link
              className={styles.linkButton}
              to={`/consultations/${ticket.encounter.id}`}
            >
              Encounter
            </Link>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card
        title={`My patients today · ${branch.name}`}
        flush
        actions={
          hasPermission('queue:call') && (
            <Button
              size="sm"
              variant="primary"
              icon={<Check size={14} />}
              disabled={!!active}
              title={
                active
                  ? t('myPatients.finishActiveFirst', { number: active.number })
                  : undefined
              }
              onClick={() => setDrawer('call-next')}
            >
              Call next
            </Button>
          )
        }
      >
        <div className={styles.filters} aria-label={t('myPatients.filters')}>
          <Field label={t('myPatients.status')}>
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              options={[
                { value: 'all', label: t('myPatients.allStatuses') },
                {
                  value: 'waiting',
                  label: t('myPatients.statusOption', {
                    name: STATUS.waiting,
                  }),
                },
                {
                  value: 'in_room',
                  label: t('myPatients.statusOption', {
                    name: STATUS.in_room,
                  }),
                },
                {
                  value: 'done',
                  label: t('myPatients.statusOption', { name: STATUS.done }),
                },
              ]}
            />
          </Field>
          <Field label={t('myPatients.service')}>
            <Select
              value={service}
              onChange={(event) => setService(event.target.value)}
              options={[
                { value: 'all', label: t('myPatients.allServices') },
                ...services.map(([value, label]) => ({
                  value,
                  label: t('myPatients.serviceOption', { name: label }),
                })),
              ]}
            />
          </Field>
          <Field label={t('myPatients.payer')}>
            <Select
              value={payer}
              onChange={(event) => setPayer(event.target.value)}
              options={[
                { value: 'all', label: t('myPatients.allPayers') },
                ...payers.map(([value, label]) => ({
                  value,
                  label: t('myPatients.payerOption', { name: label }),
                })),
              ]}
            />
          </Field>
          <Field label={t('myPatients.search')}>
            <Input
              type="search"
              value={search}
              placeholder={t('myPatients.searchPlaceholder')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Field>
        </div>
        <DataTable
          caption="My patients today"
          columns={columns}
          rows={rows}
          rowKey={(t) => t.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title={
                allRows.length ? t('myPatients.noMatches') : 'Nobody waiting'
              }
              body={
                allRows.length
                  ? t('myPatients.noMatchesBody')
                  : 'Patients appear here when the front desk adds them to the queue.'
              }
            />
          }
        />
      </Card>
      <RoomDialog
        open={calling}
        title="Call next"
        branchName={branch.name}
        busy={callNext.isPending}
        onClose={() => setDrawer('closed')}
        onConfirm={(room) =>
          callNext
            .mutateAsync(room)
            .then((t) => {
              toast.show({
                tone: 'success',
                title: `Number ${t.number} called to ${t.room}`,
                body: t.patient.fullName,
              });
              setDrawer('closed');
            })
            .catch(fail)
        }
      />
      {children}
    </PageContainer>
  );
}
